import React, {useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import getObjectValue from 'libs/getObjectValue'
import {TreeItem, TreeView} from "@material-ui/lab";
import {makeStyles} from "@material-ui/styles";
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import TextField from "@material-ui/core/TextField/TextField";
import Chip from "@material-ui/core/Chip";
import useDebouncedEffect from "hooks/useDebouncedEffect";
import Tr from "translations/Translations";
import Dialog from "./Dialog";
import SingleButton from "./popup/components/SingleButton";

const useStyle = makeStyles(theme => ({
    root: {
        position: "relative",
        marginBottom: 10
    },
    dialogPaper: {
        padding: theme.spacing(2),
        minWidth: theme.breakpoints.values.sm
    },
    search: {
        width: '90%'
    },
    textField: {
        '&>.MuiInputBase-root': {
            flexWrap: "nowrap",
            justifyContent: "space-between",
            cursor: "pointer"
        },
        '& .MuiChip-root': {
            margin: 3,
            maxWidth: '100%'
        },
        '& .MuiInputBase-input': {
            padding: 4,
            width: 0,
            maxWidth: 30,
            opacity: 1,
            flexGrow: 1,
            textOverflow: 'ellipsis',
            visibility: "hidden"
        }
    },
    selected: {
        background: `${theme.palette.primary.main} !important`,
        color: `${theme.palette.secondary.contrastText} !important`

    },
    max100: {
        maxWidth: "100%"
    }
}));

function SelectTreeView(
    {
        formikProps,
        id,
        label,
        items,
        multiple = false,
        noError,
        className,
        required,
        disabled,
        onChange = () => {
        },
        unmountOnExit,
        skipCheckTouched,
        useObjectInsteadOfValues,
        prefixLabelsWithKeys = false
    }) {

    const classes = useStyle();
    const [expanded, setExpanded] = React.useState([]);
    const [searchText, setSearchText] = React.useState("");
    const [itemsFiltered, setItemsFiltered] = useState(items);

    const keyToData = useMemo(() => {
        const map = new Map();
        const doItem = (item) => {
            const hasChildren = Array.isArray(item.items) && item.items.length > 0;
            map.set(item.value, {label: prefixLabelsWithKeys ? item.value + " - " + item.label : item.label, last: !hasChildren, value: item})
            if (hasChildren) {
                item.items.forEach(doItem)
            }
        }

        items.forEach(doItem)

        return map;
    }, [items, prefixLabelsWithKeys])

    useEffect(()=> {
        setSearchText("")
        setItemsFiltered(items);
    }, [items])


    const errorValue = getObjectValue(id, formikProps.errors, '')
    const touched = getObjectValue(id, formikProps.touched, !!skipCheckTouched)
    const _value = getObjectValue(id, formikProps.values, null)
    const selected = _value !== null ? multiple ? _value : [_value] : []
    const editable = getObjectValue('_editable', formikProps.values, true);

    const isError = !noError && (!!errorValue && !!touched)

    let _label = label;
    if (required) _label += '*';

    const getLabel = (node) => {
        return prefixLabelsWithKeys ? node.value + ' - ' + node.label : node.label
    }

    const renderTree = (nodes, i) => {
        return (!unmountOnExit || open) && nodes.map(node => {
            if (Array.isArray(node.items) && node.items.length > 0) {
                return (<TreeItem key={node.value} nodeId={node.value} label={getLabel(node)}>
                    {Array.isArray(node.items) && node.items.length > 0 ? renderTree(node.items, i + 1) : null}
                </TreeItem>)
            } else {
                const chosen = isSelected(node.value);
                const icon = multiple ? chosen ? <CheckBoxIcon/> : <CheckBoxOutlineBlankIcon/> : undefined;

                return (
                    <TreeItem
                        key={node.value}
                        nodeId={node.value}
                        label={getLabel(node)}
                        icon={icon}
                    />
                )
            }
        })
    }

    const handleToggle = (event, nodeIds) => {
        setExpanded(nodeIds);
    };

    const isSelected = (nodeValue) => {
        if(useObjectInsteadOfValues) {
            return selected.find(e => e.value === nodeValue) !== undefined;
        } else {
            return selected.indexOf(nodeValue) !== -1
        }
    }

    const handleSelect = (event, nodeId) => {
        const elemObject = keyToData.get(nodeId);
        if (elemObject.last) {
            if (multiple) {
                let newSelected;
                if (!isSelected(nodeId)) {
                    const elem = useObjectInsteadOfValues ? elemObject.value : nodeId;
                    newSelected = [...selected, elem];
                } else {
                    newSelected = selected.filter(x => useObjectInsteadOfValues ? x.value !== nodeId : x !== nodeId)
                }
                formikProps.setFieldValue(id, newSelected);
                onChange(newSelected)
            } else {
                formikProps.setFieldValue(id, nodeId);
                setOpen(false)
                onChange(nodeId)
            }
        }
    };

    const handleDelete = (nodeId) => {
        if (multiple) {
            const newSelected = selected.filter(x => useObjectInsteadOfValues ? x.value !== nodeId.value : x !== nodeId);
            formikProps.setFieldValue(id, newSelected);
            onChange(newSelected)
        } else {
            formikProps.setFieldValue(id, null);
            onChange(null)
        }
    }

    const [open, setOpen] = useState(false);
    const handleClick = (event) => {
        if(editable && !disabled)
            setOpen(true);
    };

    useDebouncedEffect(() => {


        if (searchText === "") {
            if (items !== itemsFiltered) {
                setItemsFiltered(items);
                setExpanded([])
            }
        } else {

            const fExp = []
            const filter = (text, nodes, parentVisible) => {
                const arr = [];
                for (const node of nodes) {
                    let includes = parentVisible || getLabel(node).search(new RegExp(text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), "i")) > -1;
                    const hasChildren = Array.isArray(node.items) && node.items.length > 0;

                    let childrenFiltered;
                    if (hasChildren) {
                        childrenFiltered = filter(text, node.items, includes);
                        if (childrenFiltered.length > 0)
                            includes = true;
                    }

                    if (includes) {
                        arr.push({label: node.label, value: node.value, items: childrenFiltered})
                        if (hasChildren) {
                            fExp.push(node.value);
                        }
                    }
                }
                return arr;
            }

            const itemsF = filter(searchText, items, false);
            setItemsFiltered(itemsF);
            setExpanded(fExp);
        }


    }, {timeout: 800, ignoreInitialCall: true}, [searchText])

    const onClosePopup = ()=> setOpen(false);

    return (<div className={classes.root} data-id={id}>
            <TextField
                fullWidth
                label={_label}
                error={isError}
                helperText={noError ? undefined : isError ? errorValue : undefined}
                value=""
                onClick={handleClick}
                classes={{
                    root: classes.textField
                }}
                InputProps={{
                    startAdornment: selected.length === 0 ? undefined : (
                        <div className={classes.max100}>
                            {selected.map(x => (
                                <Chip className={className} key={useObjectInsteadOfValues ? x.value : x} variant="outlined" onDelete={!editable || disabled ? undefined : () => handleDelete(x)} label={useObjectInsteadOfValues ? x.label : keyToData.get(x)?.label}/>))}
                        </div>
                    ),
                    endAdornment: <ExpandMoreIcon />
                }}
                disabled={!editable || disabled}
            />

            <Dialog
                classes={{paper: classes.dialogPaper}}
                onClose={onClosePopup}
                open={editable && !disabled && open}
                maxWidth="sm"
                title={<TextField className={classes.search} value={searchText} onChange={(e) => setSearchText(e.target.value)} label={Tr('search2')}></TextField>}
                dialogActions={!!multiple && <SingleButton
                    justify='flex-end'
                    label={Tr('close')}
                    onClick={onClosePopup}/>}
            >
                <TreeView
                    className={classes.root}
                    defaultCollapseIcon={<ExpandMoreIcon/>}
                    defaultExpandIcon={<ChevronRightIcon/>}
                    expanded={expanded}
                    selected={useObjectInsteadOfValues ? selected.map(x => x.value) : selected}
                    onNodeToggle={handleToggle}
                    onNodeSelect={handleSelect}
                >
                    {renderTree(itemsFiltered)}
                </TreeView>
            </Dialog>
        </div>
    )
}

SelectTreeView.propTypes = {
    className: PropTypes.string,
    formikProps: PropTypes.object,
    id: PropTypes.string.isRequired,
    label: PropTypes.string,
    items: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.shape({
                value: PropTypes.any.isRequired,
                label: PropTypes.string.isRequired,
            }),
            PropTypes.string.isRequired,
        ])
    )
}

SelectTreeView.defaultProps = {
    items: []
}

export default SelectTreeView
